import React, { useState, useEffect, createContext, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { getSidebarMenuConfig } from "./config/menu.config";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SidebarLogo from "./SidebarLogo";
import SidebarMenuItem from "./components/SidebarMenuItem";
import { useModal } from "@hooks/useModal";
import { usePermissions } from "@hooks/usePermissions";
import { useProfile } from "@features/Profile/services/GetProfile";
import { canAccessEventsRequestModule } from "@features/EventManagement/integration/permissions";
import { DASHBOARD_MENU_KEY, INSTITUTES_MENU_KEY } from "./config/menu.keys";
import { canAccessPsdaAdminDashboard } from "@utils/psdaAdminDashboardAccess";
import type { Permission, UserRole } from "@constants/user.roles";
import { PERMISSIONS } from "@constants/user.roles";
import type { RootState } from "@store/store";
import "./Sidebar.css";
import { openItemOnSideBarClick } from "./utils/openItemOnSideBarClick";
import {
	getInitialSidebarCollapsed,
	getSidebarCollapsedStorageKey,
	isCmAdminUser,
} from "@features/CmDashboard/cmAdmin";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";

// Menu item type
interface MenuItem {
	key: string;
	label: string;
	icon: React.ReactNode;
	route: string;
	children?: MenuItem[];
	requiredPermission?: string;
	/** Show item if user has ANY of these permissions (e.g. Course Builder: courses.view | labs.view | equipments.view). */
	requiredAnyOfPermissions?: string[];
	/** When set, item is visible only to users with this role (e.g. Director General). Used when no backend permission exists yet. */
	requiredRole?: UserRole;
	/** When true, `all.read` does not satisfy `requiredPermission` (action-scoped API permissions). */
	requireExplicitPermission?: boolean;
	tooltip?: string;
}

// Sidebar context for sharing collapsed state
interface SidebarContextType {
	isCollapsed: boolean;
	setIsCollapsed: (value: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType>({
	isCollapsed: false,
	setIsCollapsed: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

const Sidebar: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const { openModal } = useModal();
	const { hasPermission, hasExplicitPermission, hasRole, user, userRoles, userPermissions } = usePermissions();
	const { data: profile } = useProfile();
	const userEmail = useSelector((state: RootState) => state.user.user?.emailAddress);
	const includeEventsRequest = useMemo(
		() =>
			canAccessEventsRequestModule({
				roles: userRoles,
				organizationTypeId: profile?.organizationTypeId,
				userTypeName: user?.userType?.name,
				userPermissions: userPermissions ?? [],
			}),
		[userRoles, profile?.organizationTypeId, user?.userType?.name, userPermissions],
	);
	const menu = getSidebarMenuConfig(t, {
		includeEventsRequest,
	});
	const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

	const isCmAdmin = isCmAdminUser(user);

	// CM dashboard: collapsed by default. Other roles: expanded unless saved preference exists.
	const [isCollapsed, setIsCollapsed] = useState(() => getInitialSidebarCollapsed(user));

	useEffect(() => {
		setIsCollapsed(getInitialSidebarCollapsed(user));
	}, [user?.emailAddress, user?.roles]);

	// Persist collapsed state per role (CM vs standard admin). User is read when isCollapsed changes only,
	// so switching CM ↔ admin does not write stale collapse into the other role's storage key.
	useEffect(() => {
		const storageKey = getSidebarCollapsedStorageKey(user);
		localStorage.setItem(storageKey, JSON.stringify(isCollapsed));
		window.dispatchEvent(new CustomEvent("sidebarToggle", { detail: { isCollapsed } }));
	}, [isCollapsed]);

	const toggleCollapse = () => {
		setIsCollapsed((prev: boolean) => !prev);
		// Close all expanded menus when collapsing
		if (!isCollapsed) {
			setExpandedMenus([]);
		}
	};

	const toggleMenu = (menuKey: string) => {
		// If sidebar is collapsed and clicking on a parent menu, expand sidebar and show children
		if (isCollapsed) {
			setIsCollapsed(false);
			setExpandedMenus([menuKey]); // Expand the clicked menu
			return;
		}
		setExpandedMenus(prev => (prev.includes(menuKey) ? prev.filter(key => key !== menuKey) : [...prev, menuKey]));
	};

	const isMenuExpanded = (menuKey: string) => !isCollapsed && expandedMenus.includes(menuKey);
	const isActiveRoute = (route: string) => {
		// Special handling for Course Builder routes (exclude FAQ routes)
		if (route === "/course-builder") {
			return location.pathname.startsWith("/course-builder") && !location.pathname.startsWith("/faq");
		}
		// Course Assignment (exclude Special Initiative, which is a separate top-level item)
		if (route === "/course-assign") {
			return location.pathname === "/course-assign";
		}
		// Special handling for FAQ routes
		if (route.startsWith("/faq")) {
			return location.pathname.startsWith("/faq");
		}
		// Query-based child routes (for example rejection status and configuration module).
		if (route.includes("?")) {
			const [path, query] = route.split("?");
			if (location.pathname === path) {
				const urlParams = new URLSearchParams(location.search);
				const routeParams = new URLSearchParams(query);
				return Array.from(routeParams.entries()).every(([key, value]) => urlParams.get(key) === value);
			}
			return false;
		}
		return location.pathname === route;
	};
	const isChildActive = (children: MenuItem[]) => {
		return children?.some(child => isActiveRoute(child.route));
	};

	const psdaDashboardContext = useMemo(
		() => ({
			organizationTypeId: profile?.organizationTypeId,
			roles: userRoles,
			userTypeName: user?.userType?.name,
		}),
		[profile?.organizationTypeId, userRoles, user?.userType?.name],
	);

	const hasDashboardMenuAccess = (permissions: Permission[]): boolean => {
		return permissions.some(permission => {
			if (permission === PERMISSIONS.PSDA_ADMIN_DASHBOARD) {
				return canAccessPsdaAdminDashboard(hasPermission, psdaDashboardContext);
			}
			return hasPermission(permission);
		});
	};

	/**
	 * Filter menu items based on user permissions and optional role (e.g. Director General only)
	 */
	const filterMenuByPermissions = (items: MenuItem[]): MenuItem[] => {
		return items.filter(item => {
			// If user is not authenticated, hide all items
			if (!user) {
				return false;
			}

			// If item requires a specific role (no backend permission yet), show only when user has that role
			if (item.requiredRole) {
				return hasRole(item.requiredRole as UserRole);
			}

			// If item has children, filter them first
			if (item.children) {
				const filteredChildren = filterMenuByPermissions(item.children);
				item.children = filteredChildren;

				// If no permission required on parent, show if any children are visible
				if (!item.requiredPermission) {
					return filteredChildren.length > 0;
				}

				// Check parent permission or show if children are visible
				const hasParentPermission = hasPermission(item.requiredPermission as Permission);
				return hasParentPermission || filteredChildren.length > 0;
			}

			// If no permission required, show the item
			if (!item.requiredPermission && !item.requiredAnyOfPermissions) {
				return true;
			}

			// Show if user has any of the listed permissions (e.g. Course Builder)
			if (item.requiredAnyOfPermissions?.length) {
				return hasDashboardMenuAccess(item.requiredAnyOfPermissions as Permission[]);
			}

			// Check if user has the required permission
			const permission = item.requiredPermission as Permission;
			return item.requireExplicitPermission
				? hasExplicitPermission(permission)
				: hasPermission(permission);
		});
	};

	// Filter menu items based on permissions
	const filteredMenu = filterMenuByPermissions(menu);

	// For TEVTA and PVTC users, only show Dashboard and Institutes.
	const shouldLimitMenuItems = userEmail === "tevta@gmail.com" || userEmail === "pvtc@gmail.com";
	const finalMenu = isCmAdmin
		? menu.filter(item => item.key === DASHBOARD_MENU_KEY)
		: shouldLimitMenuItems
			? filteredMenu.filter(item => item.key === DASHBOARD_MENU_KEY || item.key === INSTITUTES_MENU_KEY)
			: filteredMenu;

	// Recursive render for menu items
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const renderMenuItems = (items: MenuItem[], _parentKey: string = "", level: number = 0) =>
		items.map(item => {
			const hasChildren = Boolean(item.children && item.children.length > 0);
			const isExpanded = isMenuExpanded(item.key);
			const isActive = isActiveRoute(item.route);
			const hasActiveChild = hasChildren && isChildActive(item.children || []);
			return (
				<SidebarMenuItem
					key={item.key}
					item={item}
					level={level}
					isExpanded={isExpanded}
					isActive={isActive}
					hasActiveChild={hasActiveChild}
					isCollapsed={isCollapsed}
					onClick={() => openItemOnSideBarClick(item, hasChildren, toggleMenu, openModal, navigate)}
					toggleMenu={toggleMenu}
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					renderChildren={(children, _unusedParentKey) => renderMenuItems(children || [], item.key, level + 1)}
				/>
			);
		});

	return (
		<SidebarContext.Provider value={{ isCollapsed, setIsCollapsed }}>
			<aside
				className={`sidebar-shell sidebar-container sidebar-bg fixed left-0 top-0 z-20 flex h-screen flex-col border-r border-white/10 text-white ${
					isCollapsed ? "sidebar-collapsed" : "sidebar-expanded"
				}`}>
				<div className="sidebar-header-wrap relative shrink-0">
					<SidebarLogo isCollapsed={isCollapsed} />
					<button
						type="button"
						onClick={toggleCollapse}
						className={`sidebar-toggle-btn ${isCollapsed ? "sidebar-toggle-btn--collapsed" : ""}`}
						aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
						<span>
							<KeyboardDoubleArrowLeftIcon fontSize="small" />
						</span>
					</button>
				</div>

				<div className="sidebar-nav-area flex min-h-0 flex-1 flex-col">
					<nav
						className={`sidebar-nav w-full flex-1 overflow-y-auto overflow-x-hidden pt-0 transition-all duration-300 ${
							isCollapsed ? "px-1" : "px-0"
						}`}>
						<ul className="mb-6 w-full space-y-0">{renderMenuItems(finalMenu)}</ul>
					</nav>
				</div>
			</aside>
		</SidebarContext.Provider>
	);
};

export default Sidebar;
